import React, {useEffect, useState} from 'react';
import WaitSpinner from '../../../../components/wait-spinner';
import { isoTimestampToUk, asMoney } from '../../../../utils';
import {useLocation, Link, useParams, useOutletContext} from 'react-router-dom';
import {useLogEntriesQueryQuery, useLogEntryDetailQueryQuery, LogEntriesQueryQuery, LogEntryKind} from "../../../../generated/graphql";
import EventIcon from '@mui/icons-material/Event';
import {NonIdealState} from "../../../../components/NonIdealState";
import {CampaignOutletContextValue} from "../../form";
import {CampaignChangesContainer, CampaignChangesTable} from "./index";
import { useExchangeRatesContext } from '../../../../contexts/exchange-rates-context';

const PAGE_SIZE = 50;

type EntryRow = NonNullable<LogEntriesQueryQuery['logEntries']>[number];
export const CampaignChangesPane = () => {
    let changeDetail;
    const { values,setTab} = useOutletContext<CampaignOutletContextValue>();
    const location = useLocation();
    const params = useParams();
    const currency = useExchangeRatesContext();
    const changeId = params.subId;
    const makeUrl = (id)=>{
        return '/campaigns/'+values.job_reference+'/changes/'+id+(location.search?location.search:'');
    }
    useEffect(()=>{
        setTab('changes');
    },[]);

    const [rows, setRows] = useState<EntryRow[]>([]);
    const [archived, setArchived] = useState(false);
    const [after, setAfter] = useState<string | null>(null);
    const [hasMoreLive, setHasMoreLive] = useState(false);

    const { data, loading, error } = useLogEntriesQueryQuery({
        variables: {
            campaignId: values?.id,
            after: after,
            archived: archived
        },
        skip: !values?.id,
        fetchPolicy: "network-only"
    });
    const changes = useLogEntryDetailQueryQuery({
        variables: {
            campaignId: values?.id,
            id: changeId
        },
        skip: !changeId,
        fetchPolicy: "network-only"
    });

    useEffect(() => {
        if (data?.logEntries) {
            const page = data.logEntries;
            setRows(prev => [...prev, ...page]);
            if (!archived) {
                setHasMoreLive(page.length >= PAGE_SIZE);
            }
        }
    }, [data]);

    const loadOlder = () => {
        if (!archived && !hasMoreLive) {
            setArchived(true);
            setAfter(null);
        } else {
            setAfter(rows[rows.length - 1]?.occurredAt ?? null);
        }
    };
    const showOlder = !loading && (hasMoreLive || archived);

    if (loading && rows.length === 0 || values?.id == 'new') {
        return <WaitSpinner variant="overlay" />;
    }
    if (error) {
        return <NonIdealState title="An error has occurred"/>;
    }
    if (!rows || !rows.length) {
        return <div>No changelog</div>
    }
    const content = rows.map(
        (k) => {
            return (<tr key={k.id} className={k.id==changeId?'active':''}>
                <td><Link to={makeUrl(k.id)}>
                    {isoTimestampToUk(k.occurredAt)}
                    </Link>
                </td>
                <td>
                    {k.kind === LogEntryKind.Event
                        ? <><EventIcon fontSize="small" className="event-icon"/>{k.label}</>
                        : k.label ?? k.user?.name}
                </td>
                <td>
                    {(currency?.targetCurrency?.glyph??'')+asMoney(k.totalPrice)}
                </td>
                <td>{k.status}</td>
            </tr>);
        }
    );
    if(changeId) {
        if(changes.loading) {
            changeDetail = <WaitSpinner variant="panel"/>;
        } else {
            if(changes.error) {
                changeDetail = <NonIdealState severity="error" title="A problem has occurred"/>;
            } else {
                const entry = changes.data?.logEntryDetail;
                if (entry?.kind === LogEntryKind.Event) {
                    const events = (entry.payload ?? []) as Array<{ code?: string; label?: string; payload?: Record<string, unknown> }>;
                    changeDetail = (
                        <div className="event-detail">
                            {events.map((ev, i) => (
                                <div key={i} className="event-item">
                                    <div className="event-label">
                                        <EventIcon fontSize="small" className="event-icon"/>{ev.label ?? ev.code}
                                    </div>
                                    {ev.payload && Object.keys(ev.payload).length > 0 && (
                                        <div className="event-payload">
                                            {Object.entries(ev.payload).map(([key, value]) => (
                                                <div key={key} className="event-payload-row">
                                                    <span className="event-payload-key">{key}</span>
                                                    <span>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {/* a "Contract updated" event carries the stored
                                        version id in its payload — link to the
                                        read-only version render (5.3) */}
                                    {ev.code === 'contract_version' &&
                                        (ev.payload as Record<string, unknown>)?.contract_version_id && (
                                            <a
                                                href={`${window.serverUri || document.location.origin}/campaign-preview/${values.job_reference}/${values.portfolio_hash}/contract-version/${(ev.payload as Record<string, unknown>)?.contract_version_id}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ marginLeft: 8 }}
                                            >
                                                View contract version
                                            </a>
                                        )}
                                </div>
                            ))}
                        </div>
                    );
                } else {
                const fields = changes.data?.logEntryDetail?.fields ?? [];
                const detailRows = [];
                let lastSubject;
                for (const f of fields) {
                    if (f.subject !== lastSubject) {
                        detailRows.push(<tr className="change-header" key={'h'+detailRows.length}><td colSpan={3}>{f.subject}</td></tr>);
                        lastSubject = f.subject;
                    }
                    detailRows.push(<tr className="change-row" key={'r'+detailRows.length}><td>{f.label ?? f.field}</td><td>{f.oldValue}</td><td>{f.newValue}</td></tr>);
                }
                changeDetail = <CampaignChangesTable className={['table'].join(' ')}>
                    <thead>
                    <tr><th>Field</th><th>Old</th><th>New</th></tr>
                    </thead>
                    <tbody>
                        {detailRows}
                    </tbody>
                    </CampaignChangesTable>;
                }
            }
        }
    } else {
        changeDetail = <div></div>;
    }
    return (
        <CampaignChangesContainer className="grid12 is-scrollable">
            <div className="grid12-5 is-scrollable">
                <CampaignChangesTable className={['table'].join(' ')}>
                    <thead>
                    <tr>
                        <th>Time/Date</th>
                        <th>Summary</th>
                        <th>Total</th>
                        <th>Status</th>
                    </tr>
                    </thead>
                    <tbody>
                        {content}
                    </tbody>
                </CampaignChangesTable>
                {showOlder && (
                    <div className="load-older">
                        <a onClick={loadOlder}>Load older</a>
                    </div>
                )}
            </div>
            <div className="grid12-7 is-scrollable">
                {changeDetail}
            </div>
        </CampaignChangesContainer>);
};
